// クラシック版の色の列と色の選択（段階 C の2 の 2a。2026-10-25 に chibi.js・画面ファイル legacy.js・gen1.js・gen1b.js から描き直した。見た目・動きは前と同じ）。
// 画面の系統（chibiConfig.SCREEN）で前の画面ファイルの違いを出す:
//   色の列 setColorList: エンジンのまま（dressup2・genesis2・kawaii・kawaii2・似顔絵）／第1世代 a・第2世代（前の legacy.js）／第1世代 b（前の gen1b.js）
//   色の選択 selColor: エンジンのまま・第2世代／第1世代 a・b（前の gen1.js。柄の欄・グラデーションの吹き出しが無く、色のボタンは ON・OFF）
// 第1世代の柄（mono、PATTERN_IN_COLOR）の selColor は gen1pattern.ts の版（main.ts があとから上書きで置く）
import type { Category } from '../types.js';
import { chibi, common } from './engine.js';
import { appendSvg, bindData, onClick, svgEl } from './dom.js';
import * as fx from './fx.js';
import type { Selection } from './selection.js';

interface Cursor {
  baseCsize: number;
  baseX: number;
  baseY: number;
  baseW: number;
  baseH: number;
  stroke: string;
  strokewidth: number;
}
/** このファイルが読み書きするエンジンの値 */
interface ColorEngine {
  isMobile: boolean;
  DIV_F_COLOR: string;
  DIV_F_COLOR_SC: string;
  DIV_F_PICKCOLOR: string;
  DIV_F_GRAD: string;
  INP_F_PICKCOLOR: string;
  SVG_COLOR: string;
  SVG_PICKCOLOR: string;
  ATTR_CCOLOR: string;
  ATTR_EDITFLOATBUTTON: string;
  ATTR_GRAD_FROM: string;
  ATTR_GRAD_TO: string;
  ATTR_GRAD_BAR: string;
  ATTR_LAYER: string;
  PATH_IMG: string;
  COLOR_ACTIVE: string;
  PARTS_CHARA: string;
  PARTS_BG: string;
  svgColor: Selection;
  svgPickColor: Selection;
  svgGrad: Selection;
  svgPattern: Selection;
  svgPickPattern: Selection;
  svgBg: Selection;
  svgMake: Selection;
  selLayer: Selection;
  cursorColor: Cursor[];
  enableEditColor: boolean;
  enableEditPattern: boolean;
  selGradPrevCol: string;
  selCategory: Category;
  groupCategory: Category[];
  patternList: { id: string }[];
  btnPickColor: unknown;
  btnEditColor: { ENABLED(valid: boolean, open: boolean): void; ON(): void; OFF(): void };
  btnEditPattern: { ENABLED(valid: boolean, open: boolean): void };
  btnMenuName: { GET(encode: boolean): [string, string]; SET(name: string, color?: string): void };
  btnSubSelect: { STATUS: number | string };
  config<T>(key: string, def: T): T;
  coreOps(): { color(cat: Category, hex: string, side: number, org: [string | null, string | null] | null): void; grad(cat: Category, from: string, to: string, side: number): void };
  setPattern(id: string): void;
  selGrad(col: string, index: number): void;
  setAllGradColor(from: string, to: string): void;
  setGradFromTo(sel: string, on?: boolean): void;
}
const E = (): ColorEngine => chibi() as unknown as ColorEngine;
type Common = { setGradColor(sel: string, offsets: number[], colors: string[]): void; convToHexRgb(c: string): string; afterChangeColor(): void };
const C = (): Common => common() as unknown as Common;

/** 画面の系統（chibiConfig.SCREEN。置かないページはエンジンのまま） */
export type Screen = 'new' | 'gen1a' | 'gen1b' | 'gen2';
export const screen = (): Screen => E().config<Screen>('SCREEN', 'new');

// ---- 色の列 ----

/**
 * 色の列（左の列のパレット）と、色を自由に選ぶボタン（カラーピッカー）を作る。aPallet: パレットの行（[0] が色）、aW・aH: 色の丸 1 つ分の大きさ。
 * 色の丸は circle.ccolorbtn#ccolor_RRGGBB（先頭は「元の色に戻す」で、色は selColor がカテゴリの色にする）、選んでいる色のカーソルは circle.SelColor が 2 つ
 */
export function setColorList(aPallet: string[][], aW: number, aH: number): void {
  const e = E(), scr = screen(), isNew = scr === 'new';
  // 色の領域の高さ（エンジンのまま: 色の列もピッカーも領域いっぱい。系統のある画面: ピッカーは正方形、残りが色の列）
  if (isNew) {
    fx.setHeight(e.DIV_F_COLOR_SC, fx.height(e.DIV_F_COLOR));
    fx.setHeight(e.DIV_F_PICKCOLOR, fx.height(e.DIV_F_COLOR));
  } else {
    fx.setHeight(e.DIV_F_PICKCOLOR, fx.width(e.DIV_F_PICKCOLOR));
    fx.setHeight(e.DIV_F_COLOR_SC, fx.height(e.DIV_F_COLOR) - fx.height(e.DIV_F_PICKCOLOR));
  }
  // カラーピッカー（浮いた丸いボタン。押すと excolor の画面）
  e.svgPickColor = fx.d3Select(appendSvg(e.DIV_F_PICKCOLOR, e.SVG_PICKCOLOR, aW, aH));
  const floatbtn = {
    LAYER: e.svgPickColor,
    CLASS: e.ATTR_EDITFLOATBUTTON,
    VISIBLE: true,
    REAR: 'circle',
    DATA: [{ BTN: e.btnPickColor, POS: { X: aW * 0.09, Y: aH * 0.05 }, SIZE: { W: aW * 0.9, H: aH * 0.9 }, SCALE: 1, COLOR: ['transparent', 'transparent'] }],
  };
  fx.colorPicker(e.INP_F_PICKCOLOR, e.PATH_IMG + 'excolor/', () => {
    if (E().enableEditColor) E().selGrad(fx.inputValue(E().INP_F_PICKCOLOR), -1);
  });
  fx.floatButton(floatbtn);
  // 色の丸（エンジンのままのスマホは横に並ぶので、svg の幅と高さを入れ替える）
  const svg = appendSvg(e.DIV_F_COLOR, e.SVG_COLOR, aW, (aPallet.length + (scr === 'gen1b' ? 4 : 7)) * aH);
  if (svg && isNew && e.isMobile) {
    const w0 = svg.getAttribute('width')!, h0 = svg.getAttribute('height')!;
    svg.setAttribute('width', h0);
    svg.setAttribute('height', w0);
  }
  e.svgColor = fx.d3Select(svg);
  if (svg) svg.setAttribute('display', 'none');
  const fwidth = aH / 12;
  const fmargin = aH / 34; // 色ボタンの間
  const cCircle = aH / 2.2; // 色ボタンの大きさ
  const cSize = aH / 2.9; // カーソルの枠の大きさ
  // 縁取り: エンジンのままはいつも、系統のある画面は COLOR_CIRCLE_STROKE（storm）のときだけ
  const stroke = isNew || e.config('COLOR_CIRCLE_STROKE', false);
  aPallet.forEach((d, i) => {
    if (!svg) return;
    const circle = svgEl('circle', { class: e.ATTR_CCOLOR + 'btn', id: e.ATTR_CCOLOR + d[0].replace('#', '_') });
    const along = i * (aH + fmargin) + fwidth + cCircle, across = fwidth + cCircle;
    // 属性の並びも前と同じに（エンジンのままは cy → cx、系統のある画面は cx → cy）
    if (isNew) {
      circle.setAttribute('cy', String(e.isMobile ? across : along));
      circle.setAttribute('cx', String(e.isMobile ? along : across));
    } else {
      circle.setAttribute('cx', String(across));
      circle.setAttribute('cy', String(along));
    }
    circle.setAttribute('r', String(cCircle));
    // 先頭は「元の色に戻す」（色はあとで selColor がカテゴリの色に）
    circle.setAttribute('style', (stroke ? 'stroke:#000;stroke-width:3px;' : '') + 'fill:' + (i === 0 ? '#000000' + (stroke ? ';' : '') : d[0]));
    onClick(circle, () => {
      if (E().enableEditColor) E().selGrad(d[0], i);
    });
    bindData(circle, d);
    svg.appendChild(circle);
  });
  // 選んでいる色のカーソル
  e.cursorColor = [0, 1].map(() => ({ baseCsize: cSize, baseX: fwidth + cCircle, baseY: fwidth + cCircle, baseW: aW, baseH: aH + fmargin, stroke: '#FFFFFF', strokewidth: fwidth }));
  e.cursorColor.forEach((dd) => {
    if (!svg) return;
    svg.appendChild(svgEl('circle', { class: 'SelColor', cx: dd.baseX, cy: dd.baseY, r: dd.baseCsize, fill: 'none', stroke: dd.stroke, 'stroke-width': dd.strokewidth }));
  });
  // スクロールバー（PC）
  if (!e.isMobile) fx.scrollbarY(e.DIV_F_COLOR_SC);
}

// ---- 色の選択（カテゴリを選んだとき） ----

/**
 * カテゴリに合わせて色の列・柄の欄・グラデーションのバーを出し入れし、「元の色に戻す」の丸をカテゴリの色にして、今の色の所までスクロールする
 */
export function selColor(aCategory: Category): void {
  const e = E(), gen1 = screen() === 'gen1a' || screen() === 'gen1b';
  const isValidCol = aCategory.COLOR.length > 0 || aCategory.GRAD.length > 0;
  const isValidGrad = aCategory.GRAD.length > 0;
  const nowValidGrad = e.svgGrad.attr('display') != 'none';
  e.enableEditColor = isValidCol;
  // 柄の欄（エンジンのまま・第2世代。CMODE の無いデータでは前と同じく例外になる）
  if (!gen1) e.enableEditPattern = aCategory.CMODE![1] != 0;
  // パレット先頭の「元の色に戻す」ボタンをカテゴリの基本色で塗る
  if (isValidCol) {
    if (aCategory.COLOR.length > 0) {
      e.svgColor.select('circle#' + e.ATTR_CCOLOR + '_').attr('style', 'fill:' + aCategory.COLOR[0] + ';stroke-opacity:1;stroke-width:2;stroke:' + (aCategory.COLOR.length > 1 ? aCategory.COLOR[1] : e.COLOR_ACTIVE));
    } else if (aCategory.GRAD.length > 0) {
      e.svgColor.select('circle#' + e.ATTR_CCOLOR + '_').attr('style', 'fill:' + aCategory.GRAD[1] + ';stroke-opacity:1;stroke-width:2;stroke:' + aCategory.GRAD[0]);
    }
  }
  // 表示状態
  fx.setCss(e.DIV_F_GRAD, 'left', fx.width(e.DIV_F_COLOR_SC));
  e.svgColor.attr('display', isValidCol ? 'inline' : 'none');
  e.svgPickColor.attr('display', isValidCol ? 'inline' : 'none');
  if (!gen1) {
    e.svgPattern.attr('display', e.enableEditPattern ? 'inline' : 'none');
    e.svgPickPattern.attr('display', e.enableEditPattern ? 'inline' : 'none');
  }
  if (isValidGrad) {
    e.svgGrad.attr('display', 'inline');
    fx.fadeIn(e.DIV_F_GRAD, 100);
  } else if (nowValidGrad) {
    fx.fadeOut(e.DIV_F_GRAD, 100, () => {
      E().svgGrad.attr('display', 'none');
    });
  }
  // 色・柄のメニューボタン（エンジンのまま・第2世代: 有効・無効と開閉。色が使えず柄だけ使えるカテゴリでは柄の欄を開く。第1世代: 色のボタンの ON・OFF）
  if (gen1) {
    if (isValidCol) e.btnEditColor.ON();
    else e.btnEditColor.OFF();
  } else {
    e.btnEditPattern.ENABLED(e.enableEditPattern, !e.enableEditColor);
    e.btnEditColor.ENABLED(e.enableEditColor, e.enableEditColor);
  }
  // 今の色の所までパレットをスクロール（パレットに無い色なら先頭へ）
  if (isValidCol) {
    const pcol = e.selLayer.attr('paramCol');
    if (pcol != '') {
      const top = fx.offsetTop('circle#' + e.ATTR_CCOLOR + pcol.replace('#', '_'));
      fx.scrollTopTo(e.DIV_F_COLOR, top !== null ? Math.max(0, top - (fx.offsetTop(e.DIV_F_COLOR) || 0)) : 0);
    }
  }
  // グラデーションのバーに今の上・下の色（未設定ならカテゴリの色）
  if (isValidGrad) {
    let pfrom = e.selLayer.attr('paramColFrom');
    let pto = e.selLayer.attr('paramColTo');
    if (pfrom == '') pfrom = aCategory.GRAD[1];
    if (pto == '') pto = aCategory.GRAD[0];
    fx.d3Select('#' + e.ATTR_GRAD_FROM + ' rect').style('fill', pfrom);
    fx.d3Select('#' + e.ATTR_GRAD_TO + ' rect').style('fill', pto);
    C().setGradColor('#' + e.ATTR_GRAD_BAR, [0, 1], [pfrom, pto]);
    // 上側を選んだ状態に
    e.setGradFromTo('#' + e.ATTR_GRAD_FROM, true);
    e.setGradFromTo('#' + e.ATTR_GRAD_TO, false);
    // 操作のヒントの吹き出し（エンジンのまま・第2世代）
    if (!gen1) {
      const tt: Record<string, string> = typeof _t !== 'undefined' && _t ? _t : {};
      fx.tooltip('GRAD', true, '#' + e.ATTR_GRAD_FROM, 'right', tt.gradient_tip ? tt.gradient_tip : 'Tap a picker to toggle its selection state.');
    }
  }
}

/** グラデーションの上・下のつまみを選ぶ（anOn を省くと切りかえ）。選んでいるつまみは三角が黒 */
export function setGradFromTo(aSel: string, anOn?: boolean): void {
  const gradsel = fx.d3Select(aSel);
  gradsel.classed('selected', anOn == undefined ? !gradsel.classed('selected') : anOn);
  gradsel.select('polygon').style('fill', gradsel.classed('selected') ? '#000000' : '#DCDCDC');
}

// ---- 色を選んだとき ----

/**
 * 選んでいるカテゴリの色を変える。sCol: 選んだ色、colIndex: パレットの位置（0 は元の色に戻す、-1 はカラーピッカーから）。
 * 色を書いて塗るのは中身（段階 B3。左右選択中なら左・右の色だけ）、後処理（旧処理）と柄の掛け直しはここ。キャラ全体のカテゴリでは名前の文字の色
 */
export function selGrad(sCol: string, colIndex: number): void {
  const e = E(), cat = e.selCategory;
  // 元の色
  let orgCol: string | null = null, orgDarkCol: string | null = null, orgGFrom: string | null = null, orgGTo: string | null = null;
  if (colIndex == 0) {
    if (cat.COLOR.length > 0) {
      orgCol = cat.COLOR[0];
      if (cat.COLOR.length > 1) orgDarkCol = cat.COLOR[1];
    }
    if (cat.GRAD.length > 0) {
      orgGFrom = cat.GRAD[1];
      orgGTo = cat.GRAD[0];
    }
  }
  const isOrg = colIndex == 0;
  if (isOrg) sCol = (orgCol != null ? orgCol : orgGFrom) as string;
  const anColId = e.ATTR_CCOLOR + sCol.replace('#', '_');
  const isSame = e.selGradPrevCol == sCol;
  e.selGradPrevCol = sCol;
  // ピッカーが 000000 か同じ色を選んだら、ピッカーの色を選んだ色に
  if (isSame || fx.inputValue(e.INP_F_PICKCOLOR).replace('#', '') == '000000') fx.setInputValue(e.INP_F_PICKCOLOR, sCol);
  // 色のカーソル（縦に並ぶ位置。スマホで横に並ぶ画面でも縦の位置を動かす＝前と同じ）
  if (colIndex >= 0 && (cat.COLOR.length > 0 || cat.GRAD.length > 0)) {
    const svg = e.svgColor.node();
    const cursors = svg ? Array.from(svg.querySelectorAll('circle.SelColor')) : [];
    fx.bounceCursor(
      cursors,
      (i) => ({ cy: e.cursorColor[i].baseY + colIndex * e.cursorColor[i].baseH, r: e.cursorColor[i].baseCsize * (i * 1.5), stroke: i == 0 ? '#882244' : '#00FFFF' }),
      (i) => ({ r: e.cursorColor[i].baseCsize, stroke: '#FFFFFF' }),
    );
  }
  // キャラ全体のカテゴリ: 名前の文字の色
  if (cat.LAYER[0] == e.PARTS_CHARA) {
    const oname = e.btnMenuName.GET(false);
    e.btnMenuName.SET(oname[0], sCol);
    return;
  }
  if (cat.COLOR.length > 0) {
    // 注意: 色ボタンは circle なので、この rect への selected 付け外しは何もしていない（元ソースからの名残）
    document.querySelectorAll(e.DIV_F_COLOR + ' rect').forEach((r) => r.classList.remove('selected'));
    const r = document.querySelector(e.DIV_F_COLOR + ' rect#' + anColId);
    if (r) r.classList.add('selected');
    const coreOps = e.coreOps();
    e.groupCategory.forEach((category) => {
      let ptnId = '';
      coreOps.color(category, sCol, Number(e.btnSubSelect.STATUS) || 0, isOrg ? [orgCol, orgDarkCol] : null);
      // 柄が付いていれば柄番号を拾う
      category.LAYER.forEach((dd) => {
        const layer = (dd == e.PARTS_BG ? e.svgBg : e.svgMake).select('#' + e.ATTR_LAYER + dd);
        const ptn = layer.attr('paramPtn');
        if (ptnId === '' && ptn != undefined && ptn !== '') ptnId = ptn;
      });
      // 肌色連動・線の色などメーカー独自の後処理
      C().afterChangeColor();
      // 柄が付いていれば新しい色で作り直す（setPattern は選んでいるカテゴリを見てグループ全体に掛けるので、グループのカテゴリの数だけ同じ処理を繰り返す。前と同じ）
      if (ptnId !== '') E().setPattern(E().patternList[Number(ptnId) - 1].id);
    });
  }
  if (cat.GRAD.length > 0) {
    // グラデーション（上・下のうち選んでいる側に色を入れる）
    let ischange = false;
    const from = fx.d3Select('#' + e.ATTR_GRAD_FROM + ' rect'), to = fx.d3Select('#' + e.ATTR_GRAD_TO + ' rect');
    if (isOrg) {
      from.style('fill', orgGFrom);
      to.style('fill', orgGTo);
      ischange = true;
    } else {
      if (fx.d3Select('#' + e.ATTR_GRAD_FROM).classed('selected')) {
        from.style('fill', sCol);
        ischange = true;
      }
      if (fx.d3Select('#' + e.ATTR_GRAD_TO).classed('selected')) {
        to.style('fill', sCol);
        ischange = true;
      }
    }
    if (ischange) {
      const cfrom = C().convToHexRgb(from.style('fill'));
      const cto = C().convToHexRgb(to.style('fill'));
      C().setGradColor('#' + e.ATTR_GRAD_BAR, [0, 1], [cfrom, cto]);
      E().setAllGradColor(cfrom, cto);
    }
  }
}

/** 選んでいるカテゴリのグループの全レイヤーにグラデーションの上・下の色（書いて塗るのは中身。左右選択中なら左・右の色にも） */
export function setAllGradColor(cfrom: string, cto: string): void {
  const e = E(), coreOps = e.coreOps();
  e.groupCategory.forEach((d) => {
    coreOps.grad(d, cfrom, cto, Number(e.btnSubSelect.STATUS) || 0);
  });
}
